import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  PriorityHigh as HighPriorityIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { ticketsApi, benutzerApi, adressenApi } from '../services/api';
import type { Ticket, Benutzer, Adresse } from '../types/api';
import { milestoneService, type Milestone } from '../services/milestoneService';
import TicketDialog from './TicketDialog';

const TodayTicketsWidget: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [users, setUsers] = useState<Benutzer[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [adressen, setAdressen] = useState<Adresse[]>([]);

  useEffect(() => {
    loadTodayTickets();
    loadUsers();
    loadMilestones();
    loadAdressen();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await benutzerApi.getMandantBenutzer();
      setUsers(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
    }
  };

  const loadMilestones = async () => {
    try {
      const data = await milestoneService.getAll();
      setMilestones(data);
    } catch (error) {
      console.error('Fehler beim Laden der Meilensteine:', error);
    }
  };

  const loadAdressen = async () => {
    try {
      const response = await adressenApi.getAll({ take: 1000 });
      setAdressen(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Adressen:', error);
    }
  };

  const loadTodayTickets = async () => {
    try {
      setLoading(true);
      setError(null);

      // Hole alle offenen und in Bearbeitung Tickets
      const response = await ticketsApi.getAll({
        take: 100 // Maximale Anzahl für Filterung
      });

      // Filtere Tickets, die heute fällig sind
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const todayEnd = new Date(today);
      todayEnd.setHours(23, 59, 59, 999);

      const todayTickets = response.data.filter((ticket) => {
        // Nur nicht erledigte Tickets
        if (ticket.status === 'Erledigt') return false;

        // Prüfe ob "wann" heute ist
        if (ticket.wann) {
          const ticketDate = new Date(ticket.wann);
          return ticketDate >= today && ticketDate <= todayEnd;
        }
        return false;
      });

      // Sortiere nach Priorität (Hoch -> Mittel -> Niedrig)
      const priorityOrder: { [key: string]: number } = {
        'Hoch': 1,
        'Mittel': 2,
        'Niedrig': 3,
      };

      todayTickets.sort((a, b) => {
        const priorityA = priorityOrder[a.prioritaet || 'Mittel'] || 2;
        const priorityB = priorityOrder[b.prioritaet || 'Mittel'] || 2;
        return priorityA - priorityB;
      });

      setTickets(todayTickets);
    } catch (err: any) {
      console.error('Fehler beim Laden der heute fälligen Tickets:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleTicketClick = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTicket(null);
  };

  const handleTicketSaved = async () => {
    // Tickets neu laden nach Änderungen
    await loadTodayTickets();
    handleCloseDialog();
  };

  const getPriorityColor = (prioritaet?: string): 'error' | 'warning' | 'default' => {
    switch (prioritaet) {
      case 'Hoch':
        return 'error';
      case 'Mittel':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string): 'default' | 'info' | 'success' => {
    switch (status) {
      case 'Offen':
        return 'default';
      case 'In Bearbeitung':
        return 'info';
      case 'Erledigt':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <CalendarIcon color="primary" />
        <Typography variant="h6">
          Heute fällige Tickets
        </Typography>
        {tickets.length > 0 && (
          <Chip
            label={tickets.length}
            color="primary"
            size="small"
          />
        )}
      </Box>

      {tickets.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            color: 'text.secondary'
          }}
        >
          <Typography variant="body1" gutterBottom>
            🎉 Keine Tickets für heute fällig!
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Sie haben alle heutigen Aufgaben erledigt oder es sind keine geplant.
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {tickets.map((ticket, index) => (
            <React.Fragment key={ticket.ticketID}>
              {index > 0 && <Divider />}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleTicketClick(ticket)}
                  sx={{
                    py: 2,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        {ticket.prioritaet === 'Hoch' && (
                          <HighPriorityIcon
                            color="error"
                            fontSize="small"
                          />
                        )}
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: ticket.status === 'Offen' ? 'bold' : 'normal',
                            flex: 1,
                          }}
                        >
                          {ticket.titel}
                        </Typography>
                        <ArrowIcon fontSize="small" color="action" />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                          <Chip
                            label={ticket.status}
                            color={getStatusColor(ticket.status)}
                            size="small"
                            sx={{ height: 20 }}
                          />
                          {ticket.prioritaet && (
                            <Chip
                              label={ticket.prioritaet}
                              color={getPriorityColor(ticket.prioritaet)}
                              size="small"
                              sx={{ height: 20 }}
                            />
                          )}
                          {ticket.wann && (
                            <Typography variant="caption" color="textSecondary">
                              Fällig: {new Date(ticket.wann).toLocaleTimeString('de-DE', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })} Uhr
                            </Typography>
                          )}
                        </Box>
                        {ticket.was && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              mt: 0.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {ticket.was}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Ticket Detail Dialog */}
      {selectedTicket && (
        <TicketDialog
          open={dialogOpen}
          ticket={selectedTicket}
          users={users}
          adressen={adressen}
          milestones={milestones}
          onClose={handleCloseDialog}
          onSave={handleTicketSaved}
        />
      )}
    </Paper>
  );
};

export default TodayTicketsWidget;
